import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useAuth } from "@/features/auth/hooks/useAuth";
import type {
  ImagingShortcut,
  ImagingStudyNotification,
  ImagingTrackingNotification,
} from "@/features/imagings/schemas/imagings";
import {
  createImagingShortcut,
  saveImagingTrackingNotification,
} from "@/features/imagings/services/imagings";
import { getReportDownloadUrl } from "@/features/imagings/services/reportSharingService";
import type { Report } from "@/features/imagings/types/reports";

import { getLongTermSignedUrlForManualReport } from "..";

/**
 * Hook para compartir informes médicos
 */
export function useReportSharing() {
  const queryClient = useQueryClient();
  const { currentUser } = useAuth();

  /**
   * Mutación para compartir un informe
   */
  const shareReportMutation = useMutation({
    mutationFn: async (
      data: ImagingStudyNotification & {
        report: Report;
        method: "email" | "ws";
        recipient: string;
        logo?: string;
      }
    ) => {
      if (!currentUser?.email) {
        throw new Error("No se encontró información del usuario");
      }

      try {
        let shortcutId;

        // Caso informe manual: generar URL firmada y shortcut
        if (data.report.type !== "ris") {
          const generatedUrl = await getLongTermSignedUrlForManualReport(
            data.report.id,
            data.report.imaging_id,
            8800
          );

          const shortcutData: Omit<ImagingShortcut, "created_at" | "expired_at"> = {
            object: "report",
            url: generatedUrl,
            durationToken: 8800,
            user: currentUser.email,
            receiver: data.recipient.toLowerCase(),
            active: true,
          };

          shortcutId = await createImagingShortcut(shortcutData);
        }

        // Siempre el ultimo paso es guardar la trazabilidad
        const saveTrackingData: Omit<ImagingTrackingNotification, "created_at"> = {
          id_tracking: "", // Ya no enviamos notificaciones proactivas, por lo que no hay messageId
          imaging: data.uid,
          object_shared: "report_access",
          status: "sended_api",
          to: data.recipient,
          type: data.method,
          manual: true,
          sender: data.institution_id,
          sct: shortcutId || "",
        };

        await saveImagingTrackingNotification(saveTrackingData);

        return {
          shortcutId,
          trackingId: "",
        };
      } catch (error) {
        console.error("Error compartiendo informe:", error);
        throw error;
      }
    },
    onSuccess: async () => {
      // Invalidar queries relacionadas con notificaciones
      await queryClient.invalidateQueries({ queryKey: ["notifications"] });
      await queryClient.invalidateQueries({ queryKey: ["trackingNotifications"] });
    },
    onError: (error) => {
      console.error("Error compartiendo informe:", error);
    },
  });

  /**
   * Mutación para obtener URL de descarga de un informe
   */
  const getDownloadUrlMutation = useMutation({
    mutationFn: async (report: Report) => {
      return getReportDownloadUrl(report);
    },
    onError: (error) => {
      console.error("Error obteniendo URL de descarga:", error);
    },
  });

  /*
   * Función helper para descargar un informe
   */
  const downloadReport = async (report: Report) => {
    try {
      const downloadUrl = await getDownloadUrlMutation.mutateAsync(report);

      // Crear un elemento anchor para descargar
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download =
        report.type === "manual" ? report.filename : `report_${report.id}.pdf`;
      link.target = "_blank";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Error descargando informe:", error);
      throw error;
    }
  };

  /**
   * Función para abrir informe en nueva pestaña
   */
  const openReportInNewTab = async (report: Report) => {
    try {
      if (report.type === "manual") {
        const downloadUrl = await getDownloadUrlMutation.mutateAsync(report);
        window.open(downloadUrl, "_blank");
      } else {
        const reportUrl = `https://services.viewmedonline.com/mreport/${report.id}`;
        window.open(reportUrl, "_blank");
      }
    } catch (error) {
      console.error("Error abriendo informe:", error);
      throw error;
    }
  };

  return {
    // Mutaciones principales
    shareReport: shareReportMutation,
    getDownloadUrl: getDownloadUrlMutation,

    downloadReport,
    openReportInNewTab,

    // Estados de carga
    isSharing: shareReportMutation.isPending,
    isGettingDownloadUrl: getDownloadUrlMutation.isPending,

    // Errores
    shareError: shareReportMutation.error,
    downloadError: getDownloadUrlMutation.error,
  };
}

/**
 * Hook para validar datos de compartir informe
 */
export function useReportSharingValidation() {
  /**
   * Valida un email
   */
  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  /**
   * Valida un número de teléfono
   */
  const validatePhoneNumber = (phone: string): boolean => {
    const phoneRegex = /^\+?[1-9]\d{1,14}$/;
    return phoneRegex.test(phone.replace(/\s|-/g, ""));
  };

  /**
   * Formatea un número de teléfono
   */
  const formatPhoneNumber = (phone: string): string => {
    const numbers = phone.replace(/\D/g, "");

    if (numbers.length === 0) return "";

    if (numbers.length <= 2) {
      return `+${numbers}`;
    } else if (numbers.length <= 5) {
      return `+${numbers.slice(0, 2)} ${numbers.slice(2)}`;
    } else if (numbers.length <= 8) {
      return `+${numbers.slice(0, 2)} ${numbers.slice(2, 5)}-${numbers.slice(5)}`;
    } else if (numbers.length <= 11) {
      return `+${numbers.slice(0, 2)} ${numbers.slice(2, 5)}-${numbers.slice(5, 8)}-${numbers.slice(8)}`;
    } else {
      return `+${numbers.slice(0, 2)} ${numbers.slice(2, 5)}-${numbers.slice(5, 8)}-${numbers.slice(8, 12)}`;
    }
  };

  /**
   * Obtiene el tipo de receptor basado en el input
   */
  const getRecipientType = (recipient: string): "email" | "phone" | "invalid" => {
    if (validateEmail(recipient)) {
      return "email";
    } else if (validatePhoneNumber(recipient)) {
      return "phone";
    } else {
      return "invalid";
    }
  };

  return {
    validateEmail,
    validatePhoneNumber,
    formatPhoneNumber,
    getRecipientType,
  };
}
